import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { MnemonicGrid } from '@/components/MnemonicGrid';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useSettingsStore } from '@/store/settings';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { AUTO_LOCK_OPTIONS, autoLockLabel } from '@/config';

interface KeystoreSummary {
  version: number;
  kdf: string;
  iterations: number;
}

/** 安全中心：改口令 / 查看助记词 / 导出身份包 / 密钥状态 / 锁定策略。 */
export function SecurityPage(): JSX.Element {
  const setAutoLockMinutes = useSettingsStore((s) => s.setAutoLockMinutes);
  const autoLockMinutes = useSettingsStore((s) => s.autoLockMinutes);

  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  const [mnemonicPw, setMnemonicPw] = useState('');
  const [words, setWords] = useState<string[] | null>(null);
  const [exportPw, setExportPw] = useState('');
  const [backup, setBackup] = useState<string | null>(null);
  const [summary, setSummary] = useState<KeystoreSummary | null>(null);

  useEffect(() => {
    void container.identity
      .keystoreSummary()
      .then((s) => setSummary(s))
      .catch(() => setSummary(null));
  }, []);

  const changePassword = async (): Promise<void> => {
    if (newPw !== confirmPw) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setBusy(true);
    try {
      await container.identity.changePassword(oldPw, newPw);
      setOldPw('');
      setNewPw('');
      setConfirmPw('');
      useUiStore.getState().pushToast('success', '口令已更新');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const reveal = async (): Promise<void> => {
    setBusy(true);
    try {
      const list = await container.identity.revealMnemonic(mnemonicPw);
      setWords(list);
      setMnemonicPw('');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const exportBackup = async (): Promise<void> => {
    setBusy(true);
    try {
      const data = await container.identity.exportEncrypted(exportPw);
      setBackup(data);
      setExportPw('');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">修改口令</h2>
        <div className="space-y-3">
          <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="当前口令" />
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="新口令（至少 6 位）" />
          <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="确认新口令" />
          <Button size="sm" loading={busy} onClick={() => void changePassword()}>
            保存
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">查看助记词</h2>
        <p className="mb-3 text-xs text-dim">需口令验证。助记词仅存在本机，请勿截图或外传。</p>
        <div className="space-y-3">
          <Input type="password" value={mnemonicPw} onChange={(e) => setMnemonicPw(e.target.value)} placeholder="当前口令" />
          <Button size="sm" loading={busy} onClick={() => void reveal()}>
            显示助记词
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">导出加密身份包</h2>
        <div className="space-y-3">
          <Input type="password" value={exportPw} onChange={(e) => setExportPw(e.target.value)} placeholder="当前口令" />
          <Button size="sm" loading={busy} onClick={() => void exportBackup()}>
            导出
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">密钥状态</h2>
        {summary ? (
          <div className="flex flex-wrap gap-2">
            <Badge tone="ok">版本 v{summary.version}</Badge>
            <Badge tone="neutral">KDF {summary.kdf}</Badge>
            <Badge tone="neutral">{summary.iterations} 次迭代</Badge>
          </div>
        ) : (
          <p className="text-xs text-dim">未读取到密钥信息</p>
        )}

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">自动锁定</h2>
        <div className="flex flex-wrap gap-2">
          {AUTO_LOCK_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setAutoLockMinutes(m)}
              className={`rounded-btn px-3 py-1.5 text-xs ${
                autoLockMinutes === m ? 'bg-accent text-[#04141c]' : 'bg-panel-2 text-dim hover:bg-line'
              }`}
            >
              {autoLockLabel(m)}
            </button>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-dim">
          当前身份：{useIdentityStore.getState().did.slice(0, 24)}…
        </p>
      </section>

      <Modal open={words !== null} title="你的助记词" description="请手抄并妥善保管，切勿截图或上传。"
        onOpenChange={(o) => { if (!o) setWords(null); }}>
        {words ? <MnemonicGrid words={words} masked copyable={false} /> : null}
      </Modal>

      <Modal open={backup !== null} title="加密身份包" description="复制后妥善保存；导入时需原口令。"
        onOpenChange={(o) => { if (!o) setBackup(null); }}>
        {backup ? (
          <textarea readOnly value={backup} className="input mono h-32 resize-none text-[11px]" />
        ) : null}
      </Modal>
    </div>
  );
}

export default SecurityPage;
